import classNames from "classnames"
import { GatsbyImage } from "gatsby-plugin-image"

import { getGatsbyImage } from "../../../../plugins/storyblok-image-plugin/src"
import { transformAssetSrc } from "../../../../plugins/storyblok-image-plugin/src/utils/transformAssetUrl"
import { getImageDimensionsFromUrl } from "../../../utils/image"

import { Color } from "@constants/V2/color"
import { getBackgroundColorClass, getColorHex } from "@utils/V2/color"
import { getAspectRatioClass } from "@utils/V2/image"
import React from "react"

type AspectRatio =
  | "1:1"
  | "4:3"
  | "3:4"
  | "16:9"
  | "9:16"
  | "fullWidth"
  | "auto"

type Props = {
  image: string
  alt: string
  width?: number
  height?: number
  loadType?: "eager" | "lazy"
  fetchPriority?: "high" | "low" | "auto"
  sizes?: string
  srcSetWidths?: number[]
  quality?: number
  rounded?: boolean
  /**
   * "auto" requires at least a width or height
   */
  aspectRatio?: AspectRatio
  className?: string
  imageObjectFit?: "contain" | "cover"
  testId?: string
  /**
   * Fills the reserved space with a solid colour while the image loads instead
   * of flashing empty — reduces perceived content shift / pop-in.
   *
   * For raster images this is passed to gatsby-plugin-image as a native
   * placeholder layer that is removed once the image paints (so it never bleeds
   * through transparent regions). For SVGs (which don't go through
   * gatsby-plugin-image) it reserves the aspect-ratio box and fills it.
   */
  placeholderColor?: Color
  /** Serves a GIF untransformed so it animates. Costs the srcset. */
  preserveAnimation?: boolean
}

const getAssetPathExtension = (assetUrl: string) => {
  const [assetPath] = assetUrl.split(/[?#]/)

  return assetPath.slice(assetPath.lastIndexOf(".")).toLowerCase()
}

type ResolvedDimensions = {
  width?: number
  height?: number
  aspectRatio?: number
}

const resolveResponsiveDimensions = (
  image: string,
  aspectRatio: AspectRatio,
  width?: number,
  height?: number
): ResolvedDimensions => {
  if (aspectRatio === "fullWidth") {
    return {
      width: width ?? Number.parseInt(getImageDimensionsFromUrl(image).width),
    }
  }

  if (aspectRatio === "auto") {
    const imageDimensions = getImageDimensionsFromUrl(image)
    const imageWidth = Number.parseInt(imageDimensions.width)
    const imageHeight = Number.parseInt(imageDimensions.height)

    if (width) {
      const ratio = imageHeight / imageWidth
      return { width, height: Math.floor(width * ratio), aspectRatio: ratio }
    }
    if (height) {
      const ratio = imageWidth / imageHeight
      return { width: Math.floor(height * ratio), height, aspectRatio: ratio }
    }
    return {}
  }

  const [aspectWidth, aspectHeight] = aspectRatio.split(":").map(Number)

  if (width) {
    const ratio = aspectHeight / aspectWidth
    return { width, height: width * ratio, aspectRatio: ratio }
  }
  if (height) {
    const ratio = aspectWidth / aspectHeight
    return { width: height * ratio, height, aspectRatio: ratio }
  }

  const imageDimensions = getImageDimensionsFromUrl(image)
  return {
    width: Number.parseInt(imageDimensions.width),
    height: Number.parseInt(imageDimensions.height),
    aspectRatio: aspectHeight / aspectWidth,
  }
}

const GatsbyStoryblokImage = ({
  image,
  alt = "",
  width,
  height,
  loadType = "lazy",
  fetchPriority,
  sizes,
  srcSetWidths,
  quality = undefined,
  rounded = false,
  aspectRatio = "4:3",
  className,
  imageObjectFit = "cover",
  testId,
  placeholderColor,
  preserveAnimation = false,
}: Props) => {
  const placeholderClass = placeholderColor
    ? getBackgroundColorClass(placeholderColor)
    : undefined
  const placeholderHex = placeholderColor
    ? getColorHex(placeholderColor)
    : undefined

  if (image.includes(".svg")) {
    // placeholderColor is the reduce-shift opt-in: reserve the box up front.
    // Fixed ratios reserve via the aspect class; "auto"/"fullWidth" have none,
    // so derive the missing side(s) from the asset's intrinsic ratio (a
    // width-only or fullWidth hero SVG otherwise reserves no height and still
    // shifts). Icons untouched.
    let svgWidth = width
    let svgHeight = height
    if (
      placeholderColor &&
      (aspectRatio === "auto" || aspectRatio === "fullWidth") &&
      /[0-9]+x[0-9]+(?=\/)/.test(image)
    ) {
      const intrinsic = getImageDimensionsFromUrl(image)
      const intrinsicWidth = Number.parseInt(intrinsic.width) || undefined
      const intrinsicHeight = Number.parseInt(intrinsic.height) || undefined
      if (intrinsicWidth && intrinsicHeight) {
        if (width && !height) {
          svgHeight = Math.round(width * (intrinsicHeight / intrinsicWidth))
        } else if (height && !width) {
          svgWidth = Math.round(height * (intrinsicWidth / intrinsicHeight))
        } else if (!width && !height) {
          svgWidth = intrinsicWidth
          svgHeight = intrinsicHeight
        }
      }
    }

    // No fetchpriority on the native SVG <img>: react/no-unknown-property autofix
    // forces camelCase, which the lowercase-only TS img augmentation rejects.
    return (
      <img
        loading={loadType}
        className={classNames(
          { "translate-x-0 overflow-hidden rounded": rounded },
          "max-w-full",
          placeholderColor
            ? [
                getAspectRatioClass(aspectRatio),
                "w-full object-contain",
                placeholderClass,
              ]
            : ["aspect-auto", height ? "h-[revert-layer]" : "h-auto"],
          className
        )}
        src={transformAssetSrc(image)}
        alt={alt}
        width={svgWidth}
        height={svgHeight}
      />
    )
  }

  // No placeholderColor: nothing removes it on paint, so it would show through.
  if (preserveAnimation && getAssetPathExtension(image) === ".gif") {
    return (
      <img
        loading={loadType}
        // Spread: as a JSX attribute eslint autofixes to camelCase, which React 18 rejects.
        {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
        className={classNames(
          { "translate-x-0 overflow-hidden rounded": rounded },
          "max-w-full",
          getAspectRatioClass(aspectRatio),
          imageObjectFit === "contain" ? "object-contain" : "object-cover",
          testId,
          className
        )}
        src={transformAssetSrc(image)}
        alt={alt}
        width={width}
        height={height}
      />
    )
  }

  if (width && height) {
    const imageProps = getGatsbyImage(image, {
      width,
      height,
      layout: "fixed",
      quality,
      backgroundColor: placeholderHex,
    })
    return (
      <GatsbyImage
        image={imageProps}
        alt={alt}
        loading={loadType}
        sizes={sizes}
        fetchpriority={fetchPriority}
        className={classNames({ "translate-x-0 rounded": rounded }, className)}
        imgStyle={{ objectFit: imageObjectFit }}
        imgClassName={testId}
      />
    )
  }

  const dimensions = resolveResponsiveDimensions(
    image,
    aspectRatio,
    width,
    height
  )
  const imageProps = getGatsbyImage(image, {
    width: dimensions.width,
    height: dimensions.height,
    layout: aspectRatio === "fullWidth" ? "fullWidth" : "constrained",
    quality,
    aspectRatio: dimensions.aspectRatio,
    srcSetWidths,
    backgroundColor: placeholderHex,
  })

  return (
    <GatsbyImage
      image={imageProps}
      alt={alt}
      loading={loadType}
      sizes={sizes}
      fetchpriority={fetchPriority}
      className={classNames(
        { "translate-x-0 overflow-hidden rounded": rounded },
        "max-w-full",
        className
      )}
      imgStyle={{ objectFit: imageObjectFit }}
      imgClassName={classNames(testId, getAspectRatioClass(aspectRatio))}
    />
  )
}

export default React.memo(GatsbyStoryblokImage)
